import { useEffect, useRef } from "react";

/**
 * Full-screen mint particle explosion overlay shown for ~2.5s after a deal is
 * marked Won. Emits a dense cluster of small mint dots from screen center that
 * burst outward, fade, and settle into an oval halo pattern before vanishing.
 *
 * Props:
 * - onDone(): called when the animation finishes — caller should unmount.
 * - duration: total ms (default 2400).
 */
export default function WonExplosion({ onDone, duration = 2400 }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = window.innerWidth;
    const h = window.innerHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + "px";
    canvas.style.height = h + "px";
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    const cx = w / 2;
    const cy = h / 2;
    // Generate dense particle cloud — 1400 points, biased toward an oval shape.
    const PARTICLE_COUNT = 1400;
    const particles = [];
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      // Random direction; use Gaussian-ish radius for natural density falloff.
      const angle = Math.random() * Math.PI * 2;
      const radiusBias = Math.pow(Math.random(), 0.45); // pulls denser at edges
      const targetR = radiusBias * Math.min(w, h) * 0.34;
      // Oval shape: stretch Y slightly.
      const ovalX = 1.0;
      const ovalY = 1.35;
      const vx = Math.cos(angle) * targetR * ovalX;
      const vy = Math.sin(angle) * targetR * ovalY;
      particles.push({
        // Final resting position (relative to center).
        targetX: vx,
        targetY: vy,
        // Animated position starts at 0,0.
        x: 0,
        y: 0,
        // Subtle drift after settling so the cloud breathes.
        drift: (Math.random() - 0.5) * 0.3,
        size: Math.random() < 0.85 ? 1 : Math.random() < 0.97 ? 1.5 : 2,
        // Random per-particle delay (most fire near t=0).
        delay: Math.pow(Math.random(), 4) * 180,
      });
    }

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const render = (now) => {
      if (startRef.current == null) startRef.current = now;
      const elapsed = now - startRef.current;
      const progress = Math.min(1, elapsed / duration);

      ctx.clearRect(0, 0, w, h);

      // Faint center glow at start, fading out.
      const glowAlpha = Math.max(0, 0.18 * (1 - progress * 2));
      if (glowAlpha > 0) {
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 220);
        grad.addColorStop(0, `rgba(110, 240, 220, ${glowAlpha})`);
        grad.addColorStop(1, "rgba(110, 240, 220, 0)");
        ctx.fillStyle = grad;
        ctx.fillRect(cx - 240, cy - 240, 480, 480);
      }

      // Fade for the second half of the animation.
      let alpha = 1;
      if (progress > 0.55) {
        alpha = Math.max(0, 1 - (progress - 0.55) / 0.45);
      }
      ctx.fillStyle = `rgba(110, 240, 220, ${alpha})`;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        const localElapsed = Math.max(0, elapsed - p.delay);
        const localProgress = Math.min(1, localElapsed / (duration * 0.55));
        const e = easeOutCubic(localProgress);
        p.x = p.targetX * e;
        p.y = p.targetY * e;
        // Late-stage drift outward.
        if (progress > 0.55) {
          p.x += p.drift * (progress - 0.55) * 50;
          p.y += p.drift * (progress - 0.55) * 50;
        }
        ctx.fillRect(cx + p.x - p.size / 2, cy + p.y - p.size / 2, p.size, p.size);
      }

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(render);
      } else {
        onDone && onDone();
      }
    };

    rafRef.current = requestAnimationFrame(render);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [duration, onDone]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        pointerEvents: "none",
        background: "rgba(10, 10, 10, 0.55)",
        animation: "won-fade 2.4s ease-out forwards",
      }}
      data-testid="won-explosion"
    >
      <canvas ref={canvasRef} style={{ display: "block" }} />
      <style>{`
        @keyframes won-fade {
          0% { background: rgba(10,10,10,0); }
          15% { background: rgba(10,10,10,0.55); }
          75% { background: rgba(10,10,10,0.55); }
          100% { background: rgba(10,10,10,0); }
        }
      `}</style>
    </div>
  );
}
